/**
 * Channex-Preflight (G2) — client-seitige Prüfung der Voraussetzungen vor dem
 * Publish. Verifiziert wird, was die PropertyResponse (+ Objekt-Reviewstatus)
 * hergibt; die finale Channex-Konfiguration validiert der Server beim Provisionieren.
 */
import {
  propertyStatusOptions,
  type PropertyGroupResponse,
  type PropertyResponse,
} from '@/shared/api';

export interface PreflightCheck {
  key: string;
  label: string;
  ok: boolean;
  detail: string;
}

const statusLabel = (status: PropertyResponse['status']): string =>
  propertyStatusOptions.find((o) => o.value === status)?.label ?? status;

export function buildPreflightChecks(
  property: PropertyResponse,
  group: PropertyGroupResponse | undefined,
): PreflightCheck[] {
  const a = property.address;
  const addressComplete = Boolean(
    a.streetName && a.houseNumber && a.city && a.postalCode && a.countryCode,
  );
  return [
    {
      key: 'address',
      label: 'Adresse vollständig',
      ok: addressComplete,
      detail: `${a.street}, ${a.postalCode} ${a.city}`,
    },
    {
      key: 'review',
      label: 'Adresse freigegeben',
      ok: group ? !group.needsAddressReview || addressComplete : true,
      detail: group?.needsAddressReview
        ? addressComplete
          ? 'Vollständig manuell ergänzt'
          : 'Objektadresse ist unvollständig'
        : 'Bestätigt',
    },
    {
      key: 'lifecycle',
      label: 'Lifecycle',
      ok: property.status !== 'Archived',
      detail: statusLabel(property.status),
    },
    {
      key: 'capacity',
      label: 'Kapazität',
      ok: property.occupancyAdults >= 1,
      detail: `${property.occupancyAdults + property.occupancyChildren} Schlafplätze · ${property.bedrooms} Zi.`,
    },
    {
      key: 'price',
      label: 'Preis',
      ok: property.baseNightlyRate > 0,
      detail: `${property.baseNightlyRate} ${property.currency}/Nacht`,
    },
    {
      key: 'currency',
      label: 'Währung',
      ok: property.currency.length === 3,
      detail: property.currency || '—',
    },
    {
      key: 'timezone',
      label: 'Zeitzone',
      ok: property.timezone.trim().length > 0,
      detail: property.timezone || '—',
    },
    {
      // Die eigentliche Bedingung ist „das Objekt hat noch keine Parent-Bindung" — die stellt der
      // Banner fest, bevor er diesen Dialog überhaupt öffnet. Diese Zeile ist die schwächere
      // Zweitprüfung: `channexPropertyId` überlebt seit dem Refactor auch ein `Failed` (die
      // Vendor-Ressource existiert dann), sie unterscheidet also nicht mehr zwischen „verbunden"
      // und „angelegt, aber gescheitert". Erreichbar ist sie heute nicht; wer sie schärfen will,
      // nimmt den Bindungszustand aus `property-groups/{id}`, nicht dieses Feld.
      key: 'remote',
      label: 'Noch nicht bei Channex',
      ok: !property.channexPropertyId,
      detail: property.channexPropertyId ? 'Bereits verbunden' : 'Bereit zum Anlegen',
    },
  ];
}

export function preflightPasses(checks: PreflightCheck[]): boolean {
  return checks.every((c) => c.ok);
}
